import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import { EnvironmentTypes } from '@common/constants';
import { getErrorMessage } from '@common/utils';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import RouterWrapper from './router';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || EnvironmentTypes.PRODUCTION,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ['*'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  // Better error grouping for similar issues
  attachStacktrace: true,

  // Allow for custom fingerprinting rules
  beforeSend(event, hint) {
    // Add release information when available in env
    if (import.meta.env.VITE_APP_VERSION) {
      event.release = import.meta.env.VITE_APP_VERSION;
    }

    // TODO: Remove this filter after fixing the root cause
    // Related tickets should address:
    // 1. Uncompressed Asset errors - likely build/CDN compression configuration
    // 2. Large Render Blocking Asset errors - code splitting and lazy loading optimization
    const error = hint?.originalException as any;
    const message = getErrorMessage(error);

    // Filter out asset loading errors (temporary fix to reduce Sentry noise)
    const assetErrorPatterns = ['Uncompressed Asset', 'Large Render Blocking Asset'];

    if (
      assetErrorPatterns.some((pattern) => message.toLowerCase().includes(pattern.toLowerCase()))
    ) {
      return null;
    }

    return event;
  },

  // Custom error tags
  initialScope: {
    tags: {
      app: 'chat',
      environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
    },
  },
});

// Only enabled in staging and production environments (not development)
if (import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT) {
  try {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      person_profiles: 'identified_only',
      // Only enable session recording in production (not staging)
      disable_session_recording: import.meta.env.MODE !== EnvironmentTypes.PRODUCTION,
      loaded: (posthogInstance) => {
        // Register super properties that will be sent with all events
        posthogInstance.register({
          app: 'chat',
          environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
        });
      },
    });
  } catch {
    // Analytics failure should not break the application
  }
}

const queryClient = new QueryClient();

function Root() {
  useEffect(() => {
    const getClientIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const { ip } = await response.json();
        iframeLocalStorage.setItem('client_ip', ip);
      } catch (error) {
        console.error('Failed to get client IP:', error);
      }
    };

    getClientIP();
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{
            zIndex: 999999999,
            width: 'auto',
            minWidth: '340px',
            maxWidth: '520px',
            fontSize: '0.875rem',
          }}
        />
        <RouterProvider router={RouterWrapper()} />
      </QueryClientProvider>
    </PostHogProvider>
  );
}

export default Root;
